import { useState, type ReactNode } from 'react';

import { useTimeoutEffect } from '@react-hookz/web';

import { cn } from '@/lib/utils';
import { InteractiveBackground } from './InteractiveBackground';
import { T } from 'gt-react';

const CONTENT_DELAY_MS = 1000;

interface AuthShellProps {
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  washOut?: boolean;
}

export function AuthShell({
  title,
  description,
  children,
  washOut,
}: AuthShellProps) {
  const [contentVisible, setContentVisible] = useState(!!washOut);

  useTimeoutEffect(
    () => setContentVisible(true),
    washOut ? undefined : CONTENT_DELAY_MS,
  );

  return (
    <div className="relative min-h-screen">
      <InteractiveBackground washOut={washOut} />

      {/* Blur overlay — matches marketing site */}
      <div className="fixed inset-0 z-1 backdrop-blur-[1px] pointer-events-none" />

      <div className="relative z-2 min-h-screen flex flex-col items-center justify-center px-4 py-8">
        <div
          className={cn(
            'w-full max-w-[26rem] transition-opacity duration-500',
            contentVisible ? 'opacity-100' : 'opacity-0',
          )}
        >
          <div className="flex flex-col items-center gap-8">
            <h1
              id="auth-title"
              className="font-serif text-5xl sm:text-6xl font-normal text-[#1a1a1a] leading-none"
            >
              <T context="auth">Gently</T>
            </h1>

            <div
              className={cn(
                'w-full bg-white rounded-2xl transition-shadow duration-300 p-6 sm:p-8',
                washOut
                  ? 'shadow-none delay-[600ms]'
                  : 'shadow-[0_4px_24px_rgba(0,0,0,0.06)]',
              )}
            >
              {(title || description) && (
                <div className="mb-6 space-y-2">
                  {title && (
                    <h2 className="text-2xl font-bold text-[#1a1a1a]">
                      {title}
                    </h2>
                  )}
                  {description && (
                    <p className="text-[0.95rem] text-gray-500 leading-relaxed font-light">
                      {description}
                    </p>
                  )}
                </div>
              )}

              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
